import React, { useEffect, useState } from "react";
import { Redirect, Route, Switch, useLocation } from "react-router-dom";
import { MemberSignUpV2 } from "./screens/MemberSignUp";
import { useSetWSStore, useWSStore } from "../store";
import {
  GrowthSource,
  GrowthSourceNames,
  GrowthSources
} from "../utils/growthAttribution";
import { OnboardingStep1 } from "./screens/OnboardingStep1";
import { OnboardingStep2 } from "./screens/OnboardingStep2";
import { OnboardingStep3Benefits } from "./screens/OnboardingStep3Benefits";
import { OnboardingStep3Bookkeeping } from "./screens/OnboardingStep3Bookkeeping";
import { OnboardingStep3Payments } from "./screens/OnboardingStep3Payments";
import { useCMSWebflowPartner } from "../query/cms/queries";

export const SignUpRoutes = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);

  const setStore = useSetWSStore();

  useEffect(() => {
    if (location.hash !== "") {
      setStore({ signUpHash: location.hash });
    }
  }, [location.hash]);

  useEffect(() => {
    if (location.pathname === "/member/employer-sign-up") {
      setStore({ isEmployerSignUp: true });
    }
    if (location.pathname === "/member/1099-sign-up") {
      setStore({ is1099SignUp: true });
    }
  }, [location.pathname]);

  const getGrowthSourceFromPathname = (): GrowthSource | undefined => {
    const growthUrlMatch = location.pathname.match(
      /\/([a-z])(?:\/(?:1099-)?(?:pre-)?(?:employer-)?sign-up)/
    );
    if (growthUrlMatch && growthUrlMatch[1]) {
      const cleaned = growthUrlMatch[1];
      const foundGrowthSourceName = Object.keys(GrowthSources).find(
        name => GrowthSources[name as GrowthSourceNames].cleaned === cleaned
      ) as GrowthSourceNames;
      if (foundGrowthSourceName) {
        return GrowthSources[foundGrowthSourceName];
      }
    }
    return undefined;
  };

  const [partnerNameToQuery, setPartnerNameToQuery] = useState("");

  // Pre-fetch if necessary
  useCMSWebflowPartner(partnerNameToQuery, {
    enabled: !!partnerNameToQuery
  });

  useEffect(() => {
    try {
      // Check the pathname for a growth source
      const growthSource = getGrowthSourceFromPathname();

      if (growthSource) {
        // Gather other growth data
        const growthName = params.get("growth_name") || undefined;
        const growthUserId = params.get("growth_user_id") || undefined;

        // Do different things per growth source
        if (growthSource === GrowthSources.Partner && growthName) {
          setPartnerNameToQuery(growthName);
        }

        // Save everything in the store for use later
        setStore({
          growthAttributionDetails: {
            growthSource: growthSource.source,
            growthName,
            growthUserId
          }
        });
      }
    } catch {}
  }, [location.pathname]);

  // adapt old growth capturing to new pattern
  if (params.get("partner")) {
    const isPreSignUp = location.pathname.includes("pre-sign-up");
    return (
      <Redirect
        to={`/p/${isPreSignUp ? "pre-" : ""}sign-up?growth_name=${params.get(
          "partner"
        )}`}
      />
    );
  }

  return (
    <Switch>
      {/* Pre-sign-up routes and redirects */}
      <Route
        path="/member/pre-sign-up/questionnaire/1"
        component={OnboardingStep1}
      />
      <Route
        path="/member/pre-sign-up/questionnaire/2"
        component={OnboardingStep2}
      />
      <Route
        path="/member/pre-sign-up/questionnaire/3/benefits"
        component={OnboardingStep3Benefits}
      />
      <Route
        path="/member/pre-sign-up/questionnaire/3/bookkeeping"
        component={OnboardingStep3Bookkeeping}
      />
      <Route
        path="/member/pre-sign-up/questionnaire/3/payments"
        component={OnboardingStep3Payments}
      />

      <Redirect
        from="/member/pre-sign-up/"
        exact
        to={`/member/pre-sign-up/questionnaire/1${location.search}`}
      />
      <Redirect
        from="/*/pre-sign-up/"
        to={`/member/pre-sign-up/questionnaire/1${location.search}`}
      />

      {/* Sign-up routes and redirects */}
      <Route
        path={[
          "/member/sign-up",
          "/member/employer-sign-up",
          "/member/1099-sign-up"
        ]}
        component={MemberSignUpV2}
      />

      <Redirect
        from="/*/1099-sign-up"
        to={`/member/1099-sign-up${location.search}`}
      />
      <Redirect from="/*/sign-up" to={`/member/sign-up/${location.search}`} />
    </Switch>
  );
};
